'use client';
import { useEffect, useState } from 'react';
import { useAccount } from 'wagmi';
import { DocumentsTable } from '../files/table/table';
import { Loader2 } from 'lucide-react';
import { Base_url } from '@app/config';

export function MintedLayout() {
  const { address } = useAccount();
  const [mintedDocuments, setMintedDocuments] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMintedDocs = async () => {
      try {
        setLoading(true);
        setError(null);

        const authType = localStorage.getItem('authType');
        const userEmail = localStorage.getItem('userEmail');
        const ownUserId = localStorage.getItem('own_user_id');

        console.log('MintedLayout auth check:', {
          address,
          authType,
          userEmail,
          ownUserId,
          Base_url
        });

        // Try own_user_id first (most reliable), then wallet, then email
        let response;
        let triedUserId = false;
        let triedWallet = false;
        let triedEmail = false;

        if (ownUserId) {
          triedUserId = true;
          console.log('Trying own_user_id auth with ID:', ownUserId);
          try {
            response = await fetch(`${Base_url}minted-by-user/${ownUserId}`, {
              headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
              },
            });
            console.log('User ID API response status:', response.status);
          } catch (userIdErr) {
            console.error('User ID API call failed:', userIdErr);
          }
        }

        if ((!response || !response.ok) && address) {
          triedWallet = true;
          console.log('Trying wallet auth with address:', address);
          try {
            response = await fetch(`${Base_url}minted-by-wallet/${address}`, {
              headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
              },
            });
            console.log('Wallet API response status:', response.status);
          } catch (walletErr) {
            console.error('Wallet API call failed:', walletErr);
          }
        }

        if ((!response || !response.ok) && authType === 'gmail' && userEmail) {
          triedEmail = true;
          console.log('Trying email auth with email:', userEmail);
          try {
            response = await fetch(
              `${Base_url}minted-by-email/${encodeURIComponent(userEmail)}`,
              {
                headers: {
                  'Accept': 'application/json',
                  'Content-Type': 'application/json',
                },
              }
            );
            console.log('Email API response status:', response.status);
          } catch (emailErr) {
            console.error('Email API call failed:', emailErr);
          }
        }

        console.log('Auth attempts - Wallet:', triedWallet, 'Email:', triedEmail);

        if (response && response.ok) {
          const data = await response.json();
          console.log('Minted documents API response:', response.status, data);
          console.log('Minted documents data.data:', data.data);
          console.log('Minted documents data.data length:', data.data ? data.data.length : 'N/A');

          // Transform the data to match DocumentsTable expected format
          // The API returns data in format: { data: [fileEntryWithMintedData] }
          // DocumentsTable expects: { file_entries: [fileEntry] }
          const transformedData = {
            file_entries: data.data || []
          };

          console.log('Transformed data for DocumentsTable:', transformedData);
          console.log('Final file_entries count:', transformedData.file_entries.length);

          setMintedDocuments(transformedData);
        } else {
          console.error('All API calls failed. Last response:', response);
          if (response) {
            console.error('Response status:', response.status, 'Status text:', response.statusText);
          }
          console.error('No valid authentication method found');
          setError('Failed to load minted documents - check authentication');
          setMintedDocuments({ file_entries: [] });
        }
      } catch (err: any) {
        console.error('Error in fetchMintedDocs:', err);
        setError(err?.message || 'Failed to load minted documents');
        setMintedDocuments({ file_entries: [] });
      } finally {
        setLoading(false);
      }
    };

    fetchMintedDocs();
  }, [address]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading minted documents...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center px-4">
        <div className="border-gray-300 w-full max-w-sm rounded-2xl border bg-white p-50 text-center shadow-xl">
          <p className="text-red-600 text-xl font-semibold mb-4">Error</p>
          <p className="text-gray-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {mintedDocuments && mintedDocuments?.file_entries?.length > 0 ? (
        <DocumentsTable fileEntries={mintedDocuments.file_entries} />
      ) : (
        <div className="bg-gray-100 flex h-screen items-center justify-center px-4">
          <div className="border-gray-300 w-full max-w-sm rounded-2xl border bg-white p-50 text-center shadow-xl">
            <div className="mb-6">
              <svg
                className="mx-auto h-24 w-24 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <p className="text-gray-700 text-xl font-semibold mb-2">
              No Minted Documents
            </p>
            <p className="text-gray-500 text-sm">
              Documents that are permanently stored will appear here
            </p>
          </div>
        </div>
      )}
    </>
  );
}

